﻿import {
  Alert,
  Badge,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Slide,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  Typography,
  Avatar,
  ListItemAvatar,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';

import MailIcon from '@mui/icons-material/Mail';
import TaskIcon from '@mui/icons-material/Task';
import { BreedMateNotification, BreedMateNotificationType } from 'src/api/Notification';
import { formatDate } from 'src/utils/formatDate';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { NotificationContext } from '../contexts/NotificationContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faBell,
  faEnvelope,
  faEnvelopeOpen,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import BellIcon from './BellIcon';
import { sitePaths } from '../routes/paths';
import { useNavigate } from 'react-router';
import { isUndefinedOrNull } from '../utils/dataHelpers';
import { pageAssets } from '../assets/pages';
import { useTheme, alpha } from '@mui/material/styles';

export function NotificationsButton(props: { onClick: () => void }) {
  const { unreadCount, isLoading, hasNew, clearNewStatus } = useContext(NotificationContext);
  const [animationOn, setAnimationOn] = useState(false);
  const animationTimeoutRef = useRef<number | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const { onClick } = props;

  useEffect(() => {
    if (isLoading) return;
    if (hasNew) {
      setAnimationOn(true);
      animationTimeoutRef.current = setTimeout(() => clearNewStatus(), 15000);
    } else {
      setAnimationOn(false);
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    }
  }, [hasNew, isLoading, animationTimeoutRef]);

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenSnackbar(false);
  };

  return (
    <>
      <IconButton aria-label="notifications" onClick={onClick}>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Badge badgeContent={unreadCount} max={100} color="secondary">
            <BellIcon animate={animationOn} active={animationOn} />
          </Badge>
        )}
      </IconButton>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={openSnackbar}
        onClose={handleSnackbarClose}
        slots={{ transition: (props) => <Slide {...props} direction="up" /> }}
        key="notification-snackbar"
        autoHideDuration={1200}
      >
        <Alert severity="info" variant="outlined" sx={{ width: '100%' }}>
          You have new notifications
        </Alert>
      </Snackbar>
    </>
  );
}

export function NotificationsDrawer(props: { open: boolean; onClose: () => void }) {
  const { open, onClose } = props;
  const { notifications, markAsRead, markAsHidden, isLoading } = useContext(NotificationContext);
  const navigate = useNavigate();
  const theme = useTheme();

  const getIconByType = (type: BreedMateNotificationType) => {
    switch (type) {
      case BreedMateNotificationType.Transfer:
        return pageAssets.pedigree.transfers.icon;
      case BreedMateNotificationType.Registration:
        return pageAssets.pedigree.registrations.icon;
      case BreedMateNotificationType.CalfReturn:
        return pageAssets.calfReturn.icon;
      case BreedMateNotificationType.ActiveDamList:
        return <TaskIcon />;
      case BreedMateNotificationType.HerdAudit:
        return pageAssets.herd.audit.icon;
      default:
        return <MailIcon />;
    }
  };

  const getHeaderTextByType = (type: BreedMateNotificationType) => {
    switch (type) {
      case BreedMateNotificationType.Transfer:
        return pageAssets.pedigree.transfers.notification.title;
      case BreedMateNotificationType.Registration:
        return pageAssets.pedigree.registrations.notification.title;
      case BreedMateNotificationType.CalfReturn:
        return pageAssets.calfReturn.notification.title;
      case BreedMateNotificationType.ActiveDamList:
        return pageAssets.activeDamList.notification.title;
      case BreedMateNotificationType.HerdAudit:
        return pageAssets.herd.audit.notification.title;
      default:
        return 'Notification';
    }
  };

  const handleNotificationReadClick = async (notification: BreedMateNotification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }
  };

  const getNotificationLinkUrl = (notification: BreedMateNotification) => {
    switch (notification.typeId) {
      case BreedMateNotificationType.Transfer: {
        if (notification.linkData!.isAdmin === true) {
          return sitePaths.transfer.admin.entryForm(notification.linkData!.id!);
        }
        return sitePaths.transfer.entryForm(notification.linkData!.id);
      }
      case BreedMateNotificationType.CalfReturn: {
        if (notification.linkData!.isAdmin === true) {
          return sitePaths.calf.return.admin.entryForm(
            notification.linkData!.herdId,
            notification.linkData!.year
          );
        }
        return sitePaths.calf.return.entryForm(
          notification.linkData!.herdId,
          notification.linkData!.year
        );
      }
      case BreedMateNotificationType.ActiveDamList: {
        if (notification.linkData!.isAdmin === true) {
          return sitePaths.adl.admin.entryForm(
            notification.linkData!.herdId,
            notification.linkData!.year
          );
        }
        return sitePaths.adl.entryForm(notification.linkData!.herdId, notification.linkData!.year);
      }
      case BreedMateNotificationType.HerdAudit: {
        if (notification.linkData!.isAdmin === true) {
          return sitePaths.herd.audit.entryForm(
            notification.linkData!.herdId,
            notification.linkData!.year
          );
        }
        return sitePaths.herd.audit.entryForm(
          notification.linkData!.herdId,
          notification.linkData!.year
        );
      }
      case BreedMateNotificationType.Registration: {
        if (notification.linkData!.isAdmin === true) {
          return sitePaths.registration.admin.entryForm(notification.linkData!.id);
        }
        return sitePaths.registration.entryForm(notification.linkData!.id);
      }
      default:
        return null;
    }
  };

  const handleNotificationLinkClick = async (notification: BreedMateNotification) => {
    // Handle navigation or other actions based on notification type
    // This would depend on your application's requirements
    const linkUrl: string | null = getNotificationLinkUrl(notification);

    if (!isUndefinedOrNull(linkUrl)) {
      navigate(linkUrl!);
    }
  };

  const handleNotificationHideClick = async (notification: BreedMateNotification) => {
    if (!notification.isHidden) {
      await markAsHidden(notification.id);
    }
  };

  const list = () => (
    <Box sx={{ width: 350 }} role="presentation">
      <Stack
        direction="row"
        spacing={1}
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'center',
          p: 2,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          boxShadow: theme.shadows[4], // Added shadow for better separation
          zIndex: 1, // Ensure shadow is visible above list items
          position: 'relative', // Necessary for zIndex to take effect
        }}
      >
        <FontAwesomeIcon icon={faBell} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Notifications
        </Typography>
      </Stack>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      ) : notifications && notifications.length > 0 ? (
        <List sx={{ p: 1 }}>
          {notifications.map((notification) => (
            <React.Fragment key={notification.id}>
              <ListItem
                key={notification.id}
                alignItems="flex-start"
                sx={{
                  borderLeft: !notification.isRead
                    ? `4px solid ${theme.palette.info.dark}`
                    : '4px solid transparent',
                  backgroundColor: !notification.isRead
                    ? alpha(theme.palette.info.main, 0.04)
                    : 'transparent',
                  transition: '0.2s',
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                  mb: 1,
                  '& .MuiListItemSecondaryAction-root': {
                    top: 16, // Better alignment with the avatar
                    transform: 'none',
                  },
                }}
                secondaryAction={
                  <Stack
                    direction="column"
                    spacing={1}
                    sx={{
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                    }}
                  >
                    {/* Action 2: Dismiss/Clear */}
                    <Tooltip title="Clear">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleNotificationHideClick(notification)}
                        sx={{
                          color: 'text.disabled',
                          '&:hover': { color: 'error.main' }, // Turns red on hover to indicate deletion
                        }}
                      >
                        <FontAwesomeIcon icon={faXmark} style={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>

                    {/* Action 1: View/Link */}
                    {!isUndefinedOrNull(getNotificationLinkUrl(notification)) && (
                      <Tooltip title="View Details">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleNotificationLinkClick(notification)}
                        >
                          <FontAwesomeIcon
                            icon={faArrowUpRightFromSquare}
                            style={{ fontSize: '0.9rem' }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                }
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      backgroundColor: !notification.isRead
                        ? theme.palette.primary.main
                        : theme.palette.action.disabledBackground,
                      color: theme.palette.primary.contrastText,
                      width: 36,
                      height: 36,
                      fontSize: '1rem',
                    }}
                  >
                    {getIconByType(notification.typeId)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  onClick={() => handleNotificationReadClick(notification)}
                  sx={{ cursor: 'pointer' }}
                  primary={
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getHeaderTextByType(notification.typeId)}
                    </Typography>
                  }
                  secondary={
                    <Box component="div">
                      <Typography variant="body2" sx={{
                        color: "text.secondary"
                      }}>
                        {notification.message}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ mt: 1, display: 'block', color: 'text.disabled' }}
                      >
                        {formatDate(notification.modifiedDate)}
                      </Typography>
                    </Box>
                  }
                  slotProps={{
                    secondary: { component: 'div' }
                  }}
                />
              </ListItem>
              <Divider
                variant="inset"
                component="li"
                orientation="horizontal"
                sx={{ ml: 1, mr: 1 }}
              />
            </React.Fragment>
          ))}
        </List>
      ) : (
        <Typography sx={{ p: 2 }}>No notifications</Typography>
      )}
    </Box>
  );

  return (
    <Drawer open={open} anchor={'right'} onClose={onClose}>
      {list()}
    </Drawer>
  );
}
